import React, { useContext, useEffect, useState } from 'react';
import { Box, Typography, Grid, Divider, Drawer, List, ListItem, Paper, TextField } from '@mui/material';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ThemeContext } from '../../context/ThemeContext';
import BidValue from '../../components/global/BidAsk/BidValue';
import AskValue from '../../components/global/BidAsk/AskValue';
import ProfitLoss from '../../components/global/BidAsk/ProfitLoss';
import TradeMenu from '../components/TradeMenu';
import AccountSummary from '../components/AccountSummary';
import ModifyPositionModal from '../components/ModifyPositionModal';
import { setGlobalSymbolID, setGlobalSymbolsData, setModifyPositionDetail } from '../../store/Slices/globalSymbolSlice';
import { useNavigate } from 'react-router-dom';

export default function Trade() {
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { darkMode } = useContext(ThemeContext)
  const positions = useSelector((state) => state.orderPosition.positions, shallowEqual);
  const orderSLTP = useSelector((state) => state.orderPosition.orderSLTP, shallowEqual);
  const { symbols } = useSelector(state => state.watchlist)
  // const { updatePL } = useSelector((state) => state.profitLoss);
  const [stopLosses, setStopLosses] = useState(0.00)
  const [takeProfits, setTakeProfits] = useState(0.00)
  const [showModifyModal, setShowModifyModal] = useState(false);
  const decimalPlaces = useSelector((state) => state.user.decimalPlacesForAmountFormatting);
  const [searchQuery, setSearchQuery] = useState('');

  const modifyOpen = () => {
    setShowModifyModal(true)
    handleCloseDrawer()
    dispatch(setModifyPositionDetail(selectedPosition))
  }
  const handleCloseModifyModal = () => {
    setShowModifyModal(false);
  };
  useEffect(() => {
    const selectedSLTPs = orderSLTP?.filter(sltp => sltp.positionId === selectedPosition?.id) || [];
    const sl = selectedSLTPs.map(s => s?.stopLoss?.toFixed(symbols[selectedPosition?.symbolId].digits)).join(', ') || '0.00';
    const tp = selectedSLTPs.map(s => s?.takeProfit?.toFixed(symbols[selectedPosition?.symbolId].digits)).join(', ') || '0.00';

    setStopLosses(sl);
    setTakeProfits(tp);
  }, [orderSLTP, positions, selectedPosition])

  const handlePositionClick = (position) => {
    setSelectedPosition(position);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedPosition(null);
  };

  const handleChartFromTradeTabClick = (symbol) => {
    dispatch(setGlobalSymbolsData(symbol))
    dispatch(setGlobalSymbolID(symbol.id))
    navigate("/chart");
  };

  const formattedDate = new Date(selectedPosition?.dateTime).toLocaleString('en-GB', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const filteredPositions = positions.filter((pos) => {
    const query = searchQuery.toLowerCase();
    const symbolMatch = pos.symbolName.toLowerCase().includes(query);
    const sideMatch =
      (pos.side === 1 && 'buy'.includes(query)) ||
      (pos.side === 0 && 'sell'.includes(query));
    return symbolMatch || sideMatch;
  });


  return (
    <Box p={0} sx={{ mx: 'auto', backgroundColor: darkMode ? "#151924" : 'var(--white)' }}>
      <Box sx={{ backgroundColor: darkMode ? "#151924" : "#fff" }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Paper sx={{ display: "flex", alignItems: "center", borderRadius: 0, width: "100%", padding: 0, backgroundColor: darkMode ? "#202933" : "#f1f1f1", }}>
            <TextField size="small" variant="outlined" placeholder="Search here..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} sx={{ "& fieldset": { border: "none" }, flex: 1 }} />
          </Paper>
        </Box>
      </Box>
      <AccountSummary></AccountSummary>
      {/* Positions */}
      <Box sx={{ backgroundColor: darkMode ? "#273649" : 'var(--white)', }}>
        <Typography variant="caption" color="text.secondary" px={1}>{t('positions')}</Typography>
      </Box>
      <Box sx={{ maxHeight: '430px', overflowY: 'auto' }}>
        <List disablePadding>
          {filteredPositions.length === 0 ? (
            <Typography textAlign="center" mt={2} sx={{ color: darkMode ? "var(--lightText)" : "var(--darkText)" }}>
              {t("noMatchingResults")}
            </Typography>
          ) : (
            filteredPositions.map((pos) => (
              <ListItem key={pos.id} button onClick={() => handlePositionClick(pos)} sx={{ backgroundColor: darkMode ? "#151924" : 'var(--white)', borderRadius: 1, p: 1, cursor: 'pointer' }}>
                <Box flexGrow={1} sx={{ p: 0 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ color: darkMode ? "var(--white)" : 'var(--black)' }}>
                    {pos.symbolName}{" "}
                    <span style={{ color: pos.side == 1 ? 'var(--primary)' : '#da5244' }}>{pos.side == 1 ? 'Buy' : 'Sell'} {pos.amount ? pos.amount.toFixed(decimalPlaces) : '0.00'}</span>
                  </Typography>

                  <Typography variant="caption" sx={{ color: darkMode ? "var(--lightText)" : 'var(--darkText)' }}>{pos.openPrice ? pos.openPrice.toFixed(symbols[pos.symbolId].digits) : '0.00'} → {pos.side == 1 ? <BidValue symbolId={pos.symbolId} digits={symbols[pos.symbolId].digits} /> : <AskValue symbolId={pos.symbolId} digits={symbols[pos.symbolId].digits} />}</Typography>
                </Box>
                <ProfitLoss row={pos} />
              </ListItem>
            ))
          )}
        </List>
      </Box>
      {/* Drawer for Position Details */}
      <Drawer anchor="bottom" open={drawerOpen} onClose={handleCloseDrawer}>
        <Box p={2} sx={{ backgroundColor: darkMode ? "#151924" : 'var(--white)' }}>
          {selectedPosition && (
            <>
              <Grid container justifyContent="space-between" mb={1}>
                <Typography variant="caption">{formattedDate}</Typography>
                <Typography variant="caption">{selectedPosition?.id}</Typography>
              </Grid>

              <Grid container justifyContent="space-between" my={1}>
                <Typography variant="body2" fontWeight="bold">
                  {selectedPosition?.symbolName} <span style={{ color: 'blue' }}>{selectedPosition?.type} {selectedPosition?.volume}</span>
                </Typography>
                <ProfitLoss row={selectedPosition} />
              </Grid>

              <Typography variant="caption">{selectedPosition.openPrice ? selectedPosition.openPrice.toFixed(symbols[selectedPosition.symbolId].digits) : '0.00'} → {selectedPosition?.currentPrice ? selectedPosition.currentPrice.toFixed(symbols[selectedPosition.symbolId].digits) : '0.00'}</Typography>
              <Grid container justifyContent="space-between" mt={1}>
                <Typography variant="caption">{t('stopLoss')} : {stopLosses}</Typography>
                {/* <Typography variant="caption">{t("swap")} : {selectedPosition.swap}</Typography> */}
                <Typography variant="caption">{t('takeProfit')} : {takeProfits}</Typography>

              </Grid>

              <Divider sx={{ mt: 2 }} />
            </>
          )}
        </Box>
        <TradeMenu handleOpenModifyPositionModal={modifyOpen} contextMenu={selectedPosition} setContextMenu={setSelectedPosition} style={{ fontSize: 13, textAlign: 'center', padding: '4px', minHeight: 'unset', justifyContent: 'center' }} showColumnsMenu={false} handleCloseDrawer={handleCloseDrawer} handleChartFromTradeTabClick={handleChartFromTradeTabClick} />
      </Drawer>
      <ModifyPositionModal open={showModifyModal} onClose={handleCloseModifyModal} />
    </Box>
  );
}
